import React, { useState, useEffect } from 'react';
import { orchestratorApi } from '../api/orchestrator';

// API base URL configuration
const API_BASE = process.env.NODE_ENV === "production" ? "" : "http://localhost:8090";

interface Model {
  id: string;
  name: string;
  context_length?: number;
  pricing?: {
    prompt?: string;
    completion?: string;
  };
}

interface ModelPreferences {
  id?: number;
  tenant_id: string;
  user_id?: string;
  translation_primary_model?: string;
  translation_backup_model?: string;
  ai_agent_primary_model?: string;
  ai_agent_backup_model?: string;
  custom_openrouter_key?: string;
  use_custom_key: boolean;
}

interface ModelSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  userId?: string;
}

const ModelSettings: React.FC<ModelSettingsProps> = ({ isOpen, onClose, tenantId, userId }) => {
  const [models, setModels] = useState<Model[]>([]);
  const [preferences, setPreferences] = useState<ModelPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [syncStatus, setSyncStatus] = useState<any>(null);

  // Form state
  const [translationPrimary, setTranslationPrimary] = useState('');
  const [translationBackup, setTranslationBackup] = useState('');
  const [aiAgentPrimary, setAiAgentPrimary] = useState('');
  const [aiAgentBackup, setAiAgentBackup] = useState('');
  const [customApiKey, setCustomApiKey] = useState('');
  const [useCustomKey, setUseCustomKey] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadModels();
      loadPreferences();
      loadSyncStatus();
    }
  }, [isOpen, tenantId, userId]);

  useEffect(() => {
    if (preferences) {
      setTranslationPrimary(preferences.translation_primary_model || '');
      setTranslationBackup(preferences.translation_backup_model || '');
      setAiAgentPrimary(preferences.ai_agent_primary_model || '');
      setAiAgentBackup(preferences.ai_agent_backup_model || '');
      setCustomApiKey(preferences.custom_openrouter_key || '');
      setUseCustomKey(preferences.use_custom_key || false);
    }
  }, [preferences]);

  const loadModels = async () => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/openrouter/models`, { headers: authHeaders });
      const data = await response.json();
      if (data.ok) {
        setModels(data.models);
      }
    } catch (error) {
      console.error('Failed to load models:', error);
    }
  };

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/user/model-preferences?tenantId=${tenantId}${userId ? `&userId=${userId}` : ''}`, { headers: authHeaders });
      const data = await response.json();
      if (data.ok) {
        setPreferences(data.preferences);
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSyncStatus = async () => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/openrouter/sync-status`, { headers: authHeaders });
      const data = await response.json();
      if (data.ok) {
        setSyncStatus(data);
      }
    } catch (error) {
      console.error('Failed to load sync status:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/user/model-preferences`, {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tenantId,
          userId: userId || null,
          translation_primary_model: translationPrimary || null,
          translation_backup_model: translationBackup || null,
          ai_agent_primary_model: aiAgentPrimary || null,
          ai_agent_backup_model: aiAgentBackup || null,
          custom_openrouter_key: customApiKey || null,
          use_custom_key: useCustomKey,
        }),
      });

      const data = await response.json();
      if (data.ok) {
        setPreferences(data.preferences);
        alert('Settings saved successfully!');
        onClose();
      } else {
        alert(`Failed to save settings: ${data.error}`);
      }
    } catch (error) {
      console.error('Failed to save preferences:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const filteredModels = models.filter(model =>
    model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    model.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getModelName = (modelId: string) => {
    const model = models.find(m => m.id === modelId);
    return model ? model.name : modelId;
  };

  const formatPrice = (price?: string) => {
    if (!price) return 'N/A';
    const priceNum = parseFloat(price);
    return `$${(priceNum * 1000000).toFixed(4)}/M tokens`;
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Model Settings</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
            }}
          >
            ×
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
        ) : (
          <>
            {/* Sync Status */}
            {syncStatus && (
              <div style={{
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '20px',
              }}>
                <div style={{ fontSize: '14px', color: '#0369a1' }}>
                  <strong>Model Cache:</strong> {syncStatus.totalModels} models cached
                  {syncStatus.lastSync && (
                    <span> • Last sync: {new Date(syncStatus.lastSync.synced_at).toLocaleString()}</span>
                  )}
                </div>
              </div>
            )}

            {/* Billing Notice */}
            <div style={{
              backgroundColor: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '20px',
            }}>
              <div style={{ fontSize: '14px', color: '#92400e' }}>
                <strong>💡 Model Billing:</strong> By default, we pay for translation and AI agent costs using our API keys.
                You can optionally provide your own OpenRouter API key to use premium models at your own cost.
              </div>
            </div>

            {/* Translation Models */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', marginBottom: '12px' }}>Translation Models</h3>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Primary Model
                </label>
                <select
                  value={translationPrimary}
                  onChange={(e) => setTranslationPrimary(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}
                >
                  <option value="">-- Select Primary Model --</option>
                  {filteredModels.map(model => (
                    <option key={model.id} value={model.id}>
                      {model.name} ({formatPrice(model.pricing?.prompt)})
                    </option>
                  ))}
                </select>
                {translationPrimary && (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    Selected: {getModelName(translationPrimary)}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Backup Model (fallback if primary fails)
                </label>
                <select
                  value={translationBackup}
                  onChange={(e) => setTranslationBackup(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}
                >
                  <option value="">-- Select Backup Model --</option>
                  {filteredModels.map(model => (
                    <option key={model.id} value={model.id}>
                      {model.name} ({formatPrice(model.pricing?.prompt)})
                    </option>
                  ))}
                </select>
                {translationBackup && (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    Selected: {getModelName(translationBackup)}
                  </div>
                )}
              </div>
            </div>

            {/* AI Agent Models */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', marginBottom: '12px' }}>AI Agent Models</h3>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Primary Model
                </label>
                <select
                  value={aiAgentPrimary}
                  onChange={(e) => setAiAgentPrimary(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}
                >
                  <option value="">-- Select Primary Model --</option>
                  {filteredModels.map(model => (
                    <option key={model.id} value={model.id}>
                      {model.name} ({formatPrice(model.pricing?.prompt)})
                    </option>
                  ))}
                </select>
                {aiAgentPrimary && (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    Selected: {getModelName(aiAgentPrimary)}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Backup Model (fallback if primary fails)
                </label>
                <select
                  value={aiAgentBackup}
                  onChange={(e) => setAiAgentBackup(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}
                >
                  <option value="">-- Select Backup Model --</option>
                  {filteredModels.map(model => (
                    <option key={model.id} value={model.id}>
                      {model.name} ({formatPrice(model.pricing?.prompt)})
                    </option>
                  ))}
                </select>
                {aiAgentBackup && (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    Selected: {getModelName(aiAgentBackup)}
                  </div>
                )}
              </div>
            </div>

            {/* Custom API Key */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', marginBottom: '12px' }}>Custom API Key (Optional)</h3>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={useCustomKey}
                    onChange={(e) => setUseCustomKey(e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  <span>Use my own OpenRouter API key (I pay for usage)</span>
                </label>
              </div>

              {useCustomKey && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                    OpenRouter API Key
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={customApiKey}
                      onChange={(e) => setCustomApiKey(e.target.value)}
                      placeholder="sk-or-v1-..."
                      style={{
                        width: '100%',
                        padding: '8px',
                        paddingRight: '100px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '14px',
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: '#6b7280',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      {showApiKey ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    Get your API key from{' '}
                    <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>
                      OpenRouter Dashboard
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Model Search */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Search Models ({filteredModels.length} of {models.length})
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or ID..."
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px',
                }}
              />
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '24px',
              paddingTop: '16px',
              borderTop: '1px solid #e5e7eb',
            }}>
              <button
                onClick={onClose}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ModelSettings;
